import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
    Home,
    Wallet,
    Users,
    LogOut,
    Menu,
    X,
    Share2,
    ShieldCheck,
} from "lucide-react";
import { useEffect, useState } from "react";
import SkalaLogo from "./SkalaLogo";
import { Button } from "./ui/button";
import { useLanguage } from "../contexts/LanguageContext";
import NotificationBell from "./panel/NotificationBell";

const navItems = [
    { key: "home", to: "/panel", Icon: Home, end: true },
    { key: "wallet", to: "/panel/wallet", Icon: Wallet },
    { key: "ad_accounts", to: "/panel/ad-accounts", Icon: Users },
    { key: "affiliate", to: "/panel/affiliate", Icon: Share2 },
];

const mobileTabs = navItems.slice(0, 4);

const labels = {
    en: { home: "Home", wallet: "Wallet", ad_accounts: "Accounts", affiliate: "Affiliate", management: "Admin", openAdmin: "Open admin panel", defaultUser: "User", signOut: "Sign out", unread: "unread notifications", menu: "Menu" },
    pt: { home: "Início", wallet: "Carteira", ad_accounts: "Contas", affiliate: "Afiliado", management: "Admin", openAdmin: "Abrir painel admin", defaultUser: "Usuário", signOut: "Sair", unread: "notificações não lidas", menu: "Menu" },
    es: { home: "Inicio", wallet: "Cartera", ad_accounts: "Cuentas", affiliate: "Afiliados", management: "Admin", openAdmin: "Abrir panel admin", defaultUser: "Usuario", signOut: "Salir", unread: "notificaciones sin leer", menu: "Menú" },
    ru: { home: "Главная", wallet: "Кошелёк", ad_accounts: "Аккаунты", affiliate: "Партнёрка", management: "Админ", openAdmin: "Открыть админ-панель", defaultUser: "Пользователь", signOut: "Выйти", unread: "непрочитанных уведомлений", menu: "Меню" },
    tr: { home: "Ana Sayfa", wallet: "Cüzdan", ad_accounts: "Hesaplar", affiliate: "Affiliate", management: "Yönetim", openAdmin: "Admin panelini aç", defaultUser: "Kullanıcı", signOut: "Çıkış yap", unread: "okunmamış bildirim", menu: "Menü" },
};

function MobileTabBar({ c, onMenu, menuOpen }) {
    return (
        <nav
            data-testid="panel-mobile-tabbar"
            className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-white/[0.08] bg-background/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]"
        >
            <ul className="grid grid-cols-5">
                {mobileTabs.map((it) => (
                    <li key={it.key}>
                        <NavLink
                            to={it.to}
                            end={it.end}
                            data-testid={`panel-tab-${it.key}`}
                            className={({ isActive }) =>
                                `flex flex-col items-center justify-center gap-1 h-16 text-[11px] font-medium transition-colors ${
                                    isActive && !menuOpen ? "text-primary" : "text-muted-foreground"
                                }`
                            }
                        >
                            <it.Icon className="w-5 h-5" />
                            <span>{c[it.key]}</span>
                        </NavLink>
                    </li>
                ))}
                <li>
                    <button
                        type="button"
                        onClick={onMenu}
                        data-testid="panel-tab-menu"
                        className={`w-full flex flex-col items-center justify-center gap-1 h-16 text-[11px] font-medium transition-colors ${
                            menuOpen ? "text-primary" : "text-muted-foreground"
                        }`}
                    >
                        {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        <span>{c.menu}</span>
                    </button>
                </li>
            </ul>
        </nav>
    );
}

export default function PanelLayout() {
    const { user, logout } = useAuth();
    const { language } = useLanguage();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const location = useLocation();
    const c = labels[language] || labels.en;

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    return (
        <div
            data-testid="panel-layout"
            className="min-h-screen bg-background text-foreground flex"
        >
            {/* Sidebar (desktop) / drawer (mobile "Menu") */}
            {mobileOpen && (
                <button
                    type="button"
                    aria-label="Close menu"
                    onClick={() => setMobileOpen(false)}
                    className="md:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
                />
            )}
            <aside
                className={`${
                    mobileOpen
                        ? "translate-x-0"
                        : "-translate-x-full md:translate-x-0"
                } fixed top-0 md:sticky md:top-0 z-40 h-[calc(100vh-4rem)] md:h-screen w-72 shrink-0 bg-card/95 backdrop-blur-xl border-r border-white/[0.06] flex flex-col transition-transform duration-300`}
            >
                <div className="p-6 flex items-center justify-between">
                    <Link to="/" data-testid="panel-logo">
                        <SkalaLogo />
                    </Link>
                    <button
                        className="md:hidden text-foreground p-2 -mr-2"
                        onClick={() => setMobileOpen(false)}
                        aria-label="Close menu"
                    >
                        <X size={20} />
                    </button>
                </div>

                <nav className="px-4 flex-1 space-y-1 overflow-y-auto">
                    {navItems.map((it) => (
                        <NavLink
                            key={it.key}
                            to={it.to}
                            end={it.end}
                            onClick={() => setMobileOpen(false)}
                            data-testid={`panel-nav-${it.key}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 md:py-2.5 rounded-xl text-sm transition-colors ${
                                    isActive
                                        ? "bg-primary/10 text-primary border border-primary/20"
                                        : "text-muted-foreground hover:text-foreground hover:bg-white/[0.04]"
                                }`
                            }
                        >
                            <it.Icon className="w-4 h-4" />
                            <span>{c[it.key]}</span>
                        </NavLink>
                    ))}

                    {user?.role === "admin" && (
                        <div className="pt-4 mt-4 border-t border-white/5">
                            <p className="px-4 pb-2 text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
                                {c.management}
                            </p>
                            <Link
                                to="/admin"
                                data-testid="panel-go-admin"
                                className="flex items-center gap-3 px-4 py-3 md:py-2.5 rounded-xl text-sm text-foreground/80 hover:text-foreground hover:bg-white/[0.04]"
                            >
                                <ShieldCheck className="w-4 h-4" />
                                <span>{c.openAdmin}</span>
                            </Link>
                        </div>
                    )}
                </nav>

                {/* User chip */}
                <div className="p-4 border-t border-white/5 space-y-3">
                    <div className="flex items-center gap-3 px-2">
                        <div className="w-9 h-9 rounded-full bg-primary/15 grid place-items-center text-primary font-display font-bold text-sm">
                            {(user?.full_name || "U").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold leading-tight truncate">
                                {user?.full_name || c.defaultUser}
                            </p>
                            <p className="text-[10px] font-mono text-muted-foreground tracking-wide truncate">
                                {user?.email}
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        onClick={logout}
                        data-testid="panel-logout"
                        className="w-full h-11 md:h-10 border-white/10 bg-white/[0.02] hover:bg-white/[0.06] text-foreground/85"
                    >
                        <LogOut className="w-4 h-4 mr-2" /> {c.signOut}
                    </Button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0 pb-20 md:pb-0">
                {/* Top bar (mobile) */}
                <div className="md:hidden sticky top-0 z-30 flex items-center justify-between border-b border-white/[0.06] bg-background/85 backdrop-blur-xl px-4 h-14">
                    <SkalaLogo />
                    <NotificationBell onUnreadChange={setUnreadCount} testId="panel-mobile-bell" />
                </div>

                <div className="px-4 sm:px-6 md:px-10 py-5 md:py-10 max-w-6xl">
                    <div className="hidden md:flex items-center justify-end mb-4 gap-3">
                        {unreadCount > 0 && (
                            <span className="text-xs text-foreground/85"><span className="text-primary font-semibold">{unreadCount}</span> {c.unread}</span>
                        )}
                        <div className="hidden md:block"><NotificationBell onUnreadChange={setUnreadCount} /></div>
                    </div>
                    <Outlet />
                </div>
            </main>

            <MobileTabBar c={c} onMenu={() => setMobileOpen((v) => !v)} menuOpen={mobileOpen} />
        </div>
    );
}
